import { EventQuota } from "../../../pojo/EventQuota";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { S25Util } from "../../../util/s25-util";

export namespace EditEventQuota {
    export const EMPTY_MAX_TIME = "P0DT00H00M";

    export type FormModel = {
        itemName: string;
        active: boolean;
        quotaTypeItemId: string;
        duration: number | null;
        durationType: string;
        windowMode: EventQuota.WindowMode;
        quotaStartDt: Date | null;
        quotaEndDt: Date | null;
        quantity: number | null;
        maxTimeHours: number | null;
        maxTimeMinutes: number | null;
        skipExisting: boolean;
        countByEvent: boolean;
        includeRequests: boolean;
        groupItems: boolean;
        orgScopeMode: EventQuota.OrgScopeMode;
        organizations: S25ItemI[];
        organizationSearch: EventQuota.QuotaScopeItem | null;
        locations: S25ItemI[];
        eventTypes: S25ItemI[];
        secGroups: S25ItemI[];
        comment: string;
        limitToTimeOfDay: boolean;
        dow: string;
        daySelection: boolean[];
        allowedTimeStart: string | null;
        allowedTimeEnd: string | null;
    };

    export function isOrganizationQuotaType(typeId: string | number | undefined): boolean {
        return EventQuota.toQuotaTypeId(typeId) === EventQuota.TypeIds.Organization;
    }

    export function inferOrgScopeMode(detailed: EventQuota.Detailed): EventQuota.OrgScopeMode {
        return (detailed.organizationSearches?.length ?? 0) > 0 ? "search" : "list";
    }

    function normalizeOrganizationSearch(
        search: EventQuota.QuotaScopeItem | null | undefined,
    ): EventQuota.QuotaScopeItem | null {
        if (!search?.itemId) {
            return null;
        }
        const itemName = search.itemName ?? search.txt ?? String(search.itemId);
        return {
            itemId: search.itemId,
            itemName,
            txt: search.txt ?? itemName,
            itemTypeId: search.itemTypeId ?? EventQuota.ObjectTypeIds.OrganizationSearch,
        };
    }

    function organizationSearchPayload(
        search: EventQuota.QuotaScopeItem | null | undefined,
    ): EventQuota.QuotaScopeItem | null {
        const normalized = normalizeOrganizationSearch(search);
        if (!normalized) {
            return null;
        }
        return {
            itemId: normalized.itemId,
            itemName: normalized.itemName,
            itemTypeId: normalized.itemTypeId,
        };
    }

    /** Flattens any day component into total hours for the quota editor. */
    export function isoDurationToMaxTimeHoursMinutes(isoDuration: string | null | undefined): {
        hours: number | null;
        minutes: number | null;
    } {
        if (!isoDuration || isoDuration === EMPTY_MAX_TIME) {
            return { hours: null, minutes: null };
        }
        const totalMinutes = S25Util.ISODurationToMinutes(isoDuration);
        if (totalMinutes <= 0) {
            return { hours: null, minutes: null };
        }
        return {
            hours: Math.floor(totalMinutes / 60),
            minutes: totalMinutes % 60,
        };
    }

    export function maxTimeHoursMinutesToIso(
        hours: number | null | undefined,
        minutes: number | null | undefined,
    ): string {
        const hoursValue = hours ?? 0;
        const minutesValue = minutes ?? 0;
        if (!EventQuota.hasMeaningfulMaxTimeHoursMinutes(hoursValue, minutesValue)) {
            return EMPTY_MAX_TIME;
        }
        return S25Util.daysHoursMinutesToDuration({
            days: 0,
            hours: hoursValue,
            minutes: minutesValue,
        });
    }

    export function formatMaxTimeLabel(hours: number | null | undefined, minutes: number | null | undefined): string {
        if (!EventQuota.hasMeaningfulMaxTimeHoursMinutes(hours, minutes)) {
            return "";
        }
        const parts: string[] = [];
        const hoursValue = hours ?? 0;
        const minutesValue = minutes ?? 0;
        if (hoursValue > 0) {
            parts.push(`${hoursValue} Hr${hoursValue === 1 ? "" : "s"}`);
        }
        if (minutesValue > 0) {
            parts.push(`${minutesValue} Min${minutesValue === 1 ? "" : "s"}`);
        }
        return parts.join(" and ");
    }

    /** Applies defaults and legacy type cleanup when a quota is opened in the editor. */
    export function normalizeEventQuotaModel(model: EventQuota.Detailed): void {
        model.durationType = model.durationType || "D";
        model.active = S25Util.isDefined(model.active) ? model.active : true;
        model.groupItems = S25Util.isDefined(model.groupItems) ? model.groupItems : false;
        model.skipExisting = S25Util.isDefined(model.skipExisting) ? model.skipExisting : false;
        model.countByEvent = S25Util.isDefined(model.countByEvent) ? model.countByEvent : false;
        model.includeRequests = S25Util.isDefined(model.includeRequests) ? model.includeRequests : false;
        model.dow = EventQuota.normalizeDow(model.dow);
        model.windowMode = EventQuota.normalizeWindowMode(model.windowMode);
        model.useQuantity = true;

        const legacyUserTypeIds: number[] = [
            EventQuota.TypeIds.Location,
            EventQuota.TypeIds.System,
            EventQuota.TypeIds.User,
        ];
        const typeId = EventQuota.toQuotaTypeId(model.type?.itemId);
        if (!model.type || (typeId != null && legacyUserTypeIds.includes(typeId))) {
            model.type = { readOnly: false, itemId: String(EventQuota.TypeIds.User) };
        } else {
            model.type.itemId = model.type.itemId && model.type.itemId.toString();
        }
    }

    /** Clears max booked time when count-by-event is on (time limits apply only per reservation). */
    export function clearMaxBookedTimeWhenCountByEvent(form: FormModel): FormModel {
        if (!form.countByEvent) {
            return form;
        }
        if (!EventQuota.hasMeaningfulMaxTimeHoursMinutes(form.maxTimeHours, form.maxTimeMinutes)) {
            return form;
        }
        return {
            ...form,
            maxTimeHours: null,
            maxTimeMinutes: null,
        };
    }

    export function detailedToFormModel(detailed: EventQuota.Detailed): FormModel {
        normalizeEventQuotaModel(detailed);
        const quotaDow = EventQuota.normalizeDow(detailed.dow);
        const orgScopeMode = inferOrgScopeMode(detailed);
        const countByEvent = detailed.countByEvent ?? false;
        const { hours: maxTimeHours, minutes: maxTimeMinutes } = isoDurationToMaxTimeHoursMinutes(detailed.maxTime);
        const allowedTimeStart = EventQuota.formatTimeForInput(detailed.allowedTimeStart);
        const allowedTimeEnd = EventQuota.formatTimeForInput(detailed.allowedTimeEnd);
        return {
            itemName: detailed.itemName ?? "",
            active: detailed.active ?? true,
            quotaTypeItemId: String(EventQuota.toQuotaTypeId(detailed.type?.itemId) ?? EventQuota.TypeIds.User),
            duration: detailed.duration ?? null,
            durationType: detailed.durationType ?? "D",
            windowMode: EventQuota.normalizeWindowMode(detailed.windowMode),
            quotaStartDt: EventQuota.formPickerDateFromServer(detailed.quotaStartDt),
            quotaEndDt: EventQuota.formPickerDateFromServer(detailed.quotaEndDt),
            quantity: detailed.quantity ?? null,
            maxTimeHours: countByEvent ? null : maxTimeHours,
            maxTimeMinutes: countByEvent ? null : maxTimeMinutes,
            skipExisting: detailed.skipExisting ?? false,
            countByEvent,
            includeRequests: detailed.includeRequests ?? false,
            groupItems: detailed.groupItems ?? false,
            orgScopeMode,
            organizations: [...(detailed.organizations ?? [])],
            organizationSearch: normalizeOrganizationSearch(detailed.organizationSearches?.[0]),
            locations: [...(detailed.locations ?? [])],
            eventTypes: [...(detailed.eventTypes ?? [])],
            secGroups: [...(detailed.secGroups ?? detailed.securityGroups ?? [])],
            comment: detailed.comment ?? "",
            limitToTimeOfDay: Boolean(allowedTimeStart && allowedTimeEnd),
            dow: quotaDow,
            daySelection: [...EventQuota.selectionFromDow(quotaDow)],
            allowedTimeStart,
            allowedTimeEnd,
        };
    }

    export type FormModelToDetailedOptions = {
        isNew?: boolean;
    };

    export function formModelToDetailed(
        form: FormModel,
        baseline?: EventQuota.Detailed,
        options?: FormModelToDetailedOptions,
    ): EventQuota.Detailed {
        form = clearMaxBookedTimeWhenCountByEvent(form);
        const typeReadOnly = Boolean(baseline?.type?.readOnly);
        const detailed: EventQuota.Detailed = {
            itemId: baseline?.itemId,
            itemName: form.itemName,
            active: form.active,
            type: typeReadOnly
                ? baseline!.type
                : {
                      readOnly: false,
                      itemId: form.quotaTypeItemId,
                      itemName: quotaTypeName(form.quotaTypeItemId),
                  },
            duration: EventQuota.durationForPayload(form.windowMode, form.duration),
            durationType: form.durationType,
            windowMode: form.windowMode,
            quotaStartDt: EventQuota.serverDateFromForm(form.quotaStartDt),
            quotaEndDt: EventQuota.serverDateFromForm(form.quotaEndDt),
            quantity: form.quantity ?? undefined,
            maxTime: form.countByEvent ? undefined : maxTimeHoursMinutesToIso(form.maxTimeHours, form.maxTimeMinutes),
            skipExisting: form.skipExisting,
            countByEvent: form.countByEvent,
            includeRequests: form.includeRequests,
            groupItems: form.groupItems,
            organizations: form.orgScopeMode === "list" ? [...form.organizations] : [],
            organizationSearches:
                form.orgScopeMode === "search" && form.organizationSearch
                    ? [organizationSearchPayload(form.organizationSearch)!]
                    : [],
            locations: [...form.locations],
            eventTypes: [...form.eventTypes],
            secGroups: [...form.secGroups],
            comment: form.comment,
            dow: EventQuota.dowFromSelection(form.daySelection),
            allowedTimeStart: form.limitToTimeOfDay
                ? (EventQuota.formatTimeForServer(form.allowedTimeStart) ?? undefined)
                : undefined,
            allowedTimeEnd: form.limitToTimeOfDay
                ? (EventQuota.formatTimeForServer(form.allowedTimeEnd) ?? undefined)
                : undefined,
            useQuantity: true,
            status: baseline?.status,
        };

        if (options?.isNew) {
            if (form.orgScopeMode === "list") {
                detailed.addedOrgs = [...form.organizations];
            } else if (form.organizationSearch) {
                detailed.addedOrgSearches = [organizationSearchPayload(form.organizationSearch)!];
            }
            detailed.addedLocations = [...form.locations];
            detailed.addedEventTypes = [...form.eventTypes];
            detailed.addedGroups = [...form.secGroups];
        } else if (baseline) {
            applyOrgScopeChanges(detailed, form, baseline);
            const locationDiff = diffMultiselectSelections(baseline.locations, form.locations);
            detailed.addedLocations = locationDiff.added;
            detailed.removedLocations = locationDiff.removed;

            const eventTypeDiff = diffMultiselectSelections(baseline.eventTypes, form.eventTypes);
            detailed.addedEventTypes = eventTypeDiff.added;
            detailed.removedEventTypes = eventTypeDiff.removed;

            const secGroupDiff = diffMultiselectSelections(
                baseline.secGroups ?? baseline.securityGroups,
                form.secGroups,
            );
            detailed.addedGroups = secGroupDiff.added;
            detailed.removedGroups = secGroupDiff.removed;
        }

        return detailed;
    }

    function applyOrgScopeChanges(detailed: EventQuota.Detailed, form: FormModel, baseline: EventQuota.Detailed): void {
        const baselineOrgs = baseline.organizations ?? [];
        const baselineSearches = baseline.organizationSearches ?? [];

        if (form.orgScopeMode === "list") {
            const organizationDiff = diffMultiselectSelections(baselineOrgs, form.organizations);
            detailed.addedOrgs = organizationDiff.added;
            detailed.removedOrgs = organizationDiff.removed;
            detailed.removedOrgSearches = [...baselineSearches];
            return;
        }

        detailed.removedOrgs = [...baselineOrgs];
        const baselineSearch = organizationSearchPayload(baselineSearches[0]);
        const currentSearch = organizationSearchPayload(form.organizationSearch);
        if (baselineSearch?.itemId !== currentSearch?.itemId) {
            if (baselineSearch) {
                detailed.removedOrgSearches = [baselineSearch];
            }
            if (currentSearch) {
                detailed.addedOrgSearches = [currentSearch];
            }
        }
    }

    function quotaTypeName(typeId: string | number | undefined): string {
        return isOrganizationQuotaType(typeId) ? "Organization" : "User";
    }

    function diffMultiselectSelections(
        baseline: S25ItemI[] | undefined,
        current: S25ItemI[],
    ): { added: S25ItemI[]; removed: S25ItemI[] } {
        const baselineItems = baseline ?? [];
        const baselineIds = new Set(baselineItems.map((item) => String(item.itemId)));
        const currentIds = new Set(current.map((item) => String(item.itemId)));

        const added = current.filter((item) => !baselineIds.has(String(item.itemId)));
        const removed = baselineItems.filter((item) => !currentIds.has(String(item.itemId)));
        return { added, removed };
    }
}
